import React, {
    HTMLAttributes,
    ReactElement,
    ReactNode,
    useEffect,
    useState,
} from "react";
import {
    RiAddFill, RiDraggable, RiMoreFill,
} from "react-icons/ri";
import { createPortal } from "react-dom";
import Button, { ButtonProps } from "../button/Button";

import Dropdown from "../dropdown/Dropdown";
import SortableList from "../sortable/SortableList";
import DraggableItem from "./Item";
import RouterLink from "../button/RouterLink";
import { ExtractParams, Params, PathValues } from "../../../contexts/Routing";

export interface TabItem<P extends PathValues = PathValues> {
    icon?: ReactNode;
    title: string;
    id: string;
    isPinned?: boolean;
    canShowMenu: boolean;
    params?: ExtractParams<P>;
}

type ClickHandler<P extends PathValues = PathValues> = (item: TabItem<P>, event: React.MouseEvent) => void;

export interface MenuItem {
    icon?: ReactNode;
    label: string;
    onClick: ClickHandler
}

export interface MenuItemDivider {
    isDivider: true;
}

export type MenuItemOrDivider = (MenuItem | MenuItemDivider);

function isDivider(item: MenuItemOrDivider): item is MenuItemDivider {
    return Object.hasOwn(item, "isDivider");
}

export function TabButton<P extends PathValues, SearchParams extends Params[P]>({
    item,
    isActive,
    postfixIcon,
    onClick,
    to: toValue,
    params,
    title,
    search,
}: {
    item: TabItem<P>;
    isActive: boolean;
    postfixIcon?: ReactNode;
    onClick: ButtonProps["onClick"];
    to?: P;
    params?: ExtractParams<P>;
    title?: string,
    search?: SearchParams extends never ? Record<string, string> : Partial<SearchParams>;
}): ReactElement {
    return toValue && params ? (
        <RouterLink
            size="small"
            variant="ghost-gray"
            postfixIcon={postfixIcon}
            prefixIcon={item.icon}
            isActive={isActive}
            to={toValue}
            params={params}
            search={search}
            title={title}
        >
            {item.title}
        </RouterLink>
    ) : (
        <Button
            size="small"
            variant="ghost-gray"
            postfixIcon={postfixIcon}
            prefixIcon={item.icon}
            isActive={isActive}
            onClick={onClick}
            title={title}
        >
            {item.title}
        </Button>
    );
}

TabButton.defaultProps = {
    postfixIcon: undefined,
    to: undefined,
    params: undefined,
    search: undefined,
    title: undefined,
};

export interface TabListProps<P extends PathValues, SearchParams extends Params[P] = Params[P]>
    extends Omit<HTMLAttributes<HTMLUListElement>, "children" | "onDragStart" | "onDragEnd"> {
    items: TabItem<P>[];
    onAdd?: () => void;
    onReorder?: (items: TabItem<P>[]) => void;
    onSelected?: (id: string) => void;
    selectedId?: string;
    defaultIndex?: number;
    menuItems?: MenuItemOrDivider[];
    maxCount?: number;
    to?: P;
    params?: ExtractParams<P> extends never ? never : { params: ExtractParams<P> };
    search?: SearchParams extends never ? Record<string, string> : Partial<SearchParams>
}

const DRAGGABLE_ID = "menu-items";
const TabList = React.forwardRef(<P extends PathValues = PathValues>(
    args: TabListProps<P>,
    ref: React.ForwardedRef<HTMLUListElement>,
): ReactElement => {
    const {
        items,
        className,
        onAdd,
        selectedId,
        onSelected,
        defaultIndex,
        menuItems,
        maxCount,
        onReorder,
        to,
        search,
        ...rest
    } = args;

    const [current, setCurrent] = useState(selectedId ?? (defaultIndex != null ? items[defaultIndex]?.id : undefined));

    useEffect(() => {
        setCurrent(selectedId ?? (defaultIndex != null ? items[defaultIndex]?.id : undefined));
    }, [selectedId, defaultIndex]);

    let displayItems = items;
    if (maxCount) {
        displayItems = displayItems.limit(maxCount);
    }

    const remainingItems = items.skip(displayItems.length);

    const selected = items.find(item => item.id === current);
    if (selected && !displayItems.includes(selected)) {
        displayItems.push(selected);
    }

    return (

        <SortableList
            ref={ref}
            direction="horizontal"
            id={DRAGGABLE_ID}
            items={items}
            onReorder={onReorder}
            className={`nav nav-tabs ${className}`}
            {...rest}

        >
            {displayItems.map(item => (
                <SortableList.Item
                    className="nav-link"
                    item={item}
                    key={item.id}
                >
                    {menuItems && current === item.id && item.canShowMenu ? (
                        <Dropdown>
                            <Dropdown.Toggle
                                size="small"
                                variant="ghost-gray"
                                className="text-start"
                                prefixIcon={item.icon}
                                postfixIcon={current === item.id ? <RiMoreFill /> : undefined}
                                isActive={current === item.id}
                                title={item.title}
                            >
                                {item.title}
                            </Dropdown.Toggle>
                            {createPortal(
                                <Dropdown.Menu>
                                    {menuItems.map((menuItem, index) => (
                                        isDivider(menuItem) ? (
                                            <Dropdown.Divider key={(menuItems[index - 1] as MenuItem)?.label ?? "divider"} />
                                        ) : (
                                            <Dropdown.Item
                                                key={menuItem.label}
                                                prefixIcon={menuItem.icon}
                                                onClick={event => {
                                                    menuItem.onClick(item, event);
                                                }}
                                            >
                                                {menuItem.label}
                                            </Dropdown.Item>
                                        )

                                    ))}
                                </Dropdown.Menu>,
                                document.body,

                            )}
                        </Dropdown>
                    ) : (
                        <TabButton
                            item={item}
                            key={item.id}
                            isActive={current === item.id}
                            postfixIcon={current === item.id && menuItems ? <RiMoreFill /> : undefined}
                            to={to}
                            search={search}
                            title={item.title}
                            params={item.params}
                            onClick={() => {
                                setCurrent(item.id);
                                onSelected?.(item.id);
                            }}
                        />
                    )}

                </SortableList.Item>
            ))}

            {remainingItems.isNotEmpty && (
                <li className="nav-link nav-tabs-more">

                    <Dropdown>
                        <Dropdown.Toggle
                            size="small"
                            variant="ghost-gray"
                            className="text-start"
                        >
                            {remainingItems.length}
                            {" "}
                            more...
                        </Dropdown.Toggle>
                        {createPortal(
                            <Dropdown.Menu>
                                <SortableList
                                    ref={ref}
                                    direction="vertical"
                                    id={DRAGGABLE_ID}
                                    items={items}
                                    onReorder={onReorder}
                                    {...rest}
                                >

                                    {items.map(item => (

                                        <DraggableItem
                                            id={item.id}
                                            key={item.id}
                                            prefixIcon={onReorder ? <RiDraggable /> : undefined}
                                            title={item.title}
                                            onClick={() => {
                                                setCurrent(item.id);
                                                onSelected?.(item.id);
                                            }}
                                        >
                                            <span className="m3e-1 icon-default">
                                                {item.icon}
                                            </span>
                                            {item.title}
                                        </DraggableItem>
                                    ))}
                                </SortableList>
                            </Dropdown.Menu>,
                            document.body,
                        )}

                    </Dropdown>
                </li>
            )}

            {onAdd && (
                <li className="nav-link">
                    <Button size="small" variant="ghost-gray" prefixIcon={<RiAddFill />} onClick={onAdd} />
                </li>
            )}

        </SortableList>
    );
});

TabList.defaultProps = {
    className: "",
    onAdd: undefined,
    search: undefined,
    onReorder: undefined,
};

export default TabList;
